import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { TARGET_YEARS, INDIAN_STATES, REFERRAL_SOURCES } from "@/lib/constants";
import AuthShell, { GoogleButton } from "@/pages/AuthShell";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "", target_year: "", state: "", referral_source: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") navigate(user.role === "admin" ? "/admin" : next, { replace: true });
  }, [user, navigate, next]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Account created! Welcome aboard.");
      navigate(u.role === "admin" ? "/admin" : next, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-sm border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#1F3A68] bg-white";

  return (
    <AuthShell
      title="Create Your Free Account"
      subtitle="100% free access to all Revision Kits"
      footer={<>Already have an account? <Link to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="prose-link font-semibold" data-testid="link-login">Login</Link></>}
    >
      <form onSubmit={submit} className="space-y-3.5" data-testid="register-form">
        <Field label="Full Name"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="reg-name" /></Field>
        <Field label="Email"><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="reg-email" /></Field>
        <Field label="Password"><input required type="password" value={form.password} onChange={set("password")} className={inputCls} placeholder="At least 6 characters" data-testid="reg-password" /></Field>
        <Field label="Mobile Number"><input required value={form.mobile} onChange={set("mobile")} className={inputCls} placeholder="10-digit mobile" data-testid="reg-mobile" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Target UPSC Year">
            <select required value={form.target_year} onChange={set("target_year")} className={inputCls} data-testid="reg-year">
              <option value="">Select</option>
              {TARGET_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
          <Field label="State">
            <select required value={form.state} onChange={set("state")} className={inputCls} data-testid="reg-state">
              <option value="">Select</option>
              {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <Field label="How did you hear about us?">
          <select required value={form.referral_source} onChange={set("referral_source")} className={inputCls} data-testid="reg-referral">
            <option value="">Select</option>
            {REFERRAL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        {error && <p className="rounded-sm bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#FF9933] px-4 py-2.5 text-sm font-bold text-slate-900 hover:bg-[#E68A2E] disabled:opacity-60" data-testid="register-submit">
          {loading && <Loader2 size={16} className="animate-spin" />} Create Free Account
        </button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-gray-400"><div className="h-px flex-1 bg-gray-200" /> OR <div className="h-px flex-1 bg-gray-200" /></div>
      <GoogleButton next={next} testId="register-google" />
    </AuthShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-[#0B2545]">{label}</span>
      {children}
    </label>
  );
}
